'use client';

import { cn } from '../../lib/utils';
import { hoodiePaths } from './legacyPaths';

const PE_AUTO = { pointerEvents: 'auto' } as const;

interface LegacyRainaEarsInnerProps {
  svgId: string;
  isEarTwitching?: boolean;
  onEarTwitchEnd?: () => void;
  /** Suffix for filter IDs to avoid conflicts when multiple instances exist */
  idSuffix?: string;
}

/**
 * Inner ear rendering without wrapper divs.
 * Used by both LegacyRainaHead (inline) and LegacyRainaEars (standalone for badge variant).
 */
export function LegacyRainaEarsInner({ svgId, isEarTwitching = false, onEarTwitchEnd, idSuffix = '' }: LegacyRainaEarsInnerProps) {
  const id = (name: string) => `${svgId}${name}${idSuffix}`;

  return (
    <>
      {/* Right Ear */}
      <div
        className={cn(
          'absolute inset-[8.43%_0%_76.22%_76.49%] pointer-events-none',
          isEarTwitching && 'motion-safe:animate-[raina-ear-twitch_0.3s_ease-in-out]',
        )}
        style={{ transformOrigin: 'bottom left' }}
        onAnimationEnd={(e) => {
          e.stopPropagation();
          onEarTwitchEnd?.();
        }}
      >
        <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 100.757 100.566">
          <defs>
            <filter
              colorInterpolationFilters="sRGB"
              filterUnits="userSpaceOnUse"
              height="108.233"
              id={id('rightEarFilter')}
              width="121.456"
              x="0"
              y="-7.6664"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
              <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
              <feOffset dx="20.6993" dy="-7.6664" />
              <feGaussianBlur stdDeviation="11.5" />
              <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
              <feColorMatrix type="matrix" values="0 0 0 0 0.462745 0 0 0 0 0.168627 0 0 0 0 0.980392 0 0 0 1 0" />
              <feBlend in2="shape" mode="normal" result="effect1_innerShadow" />
            </filter>
          </defs>
          <g filter={`url(#${id('rightEarFilter')})`}>
            <path d={hoodiePaths.ear} fill="#BD98FF" data-region="ear" style={PE_AUTO} />
          </g>
        </svg>
      </div>
      {/* Left Ear */}
      <div
        className={cn(
          'absolute flex inset-[8.43%_76.49%_76.22%_0%] items-center justify-center pointer-events-none',
          isEarTwitching && 'motion-safe:animate-[raina-ear-twitch_0.3s_ease-in-out]',
        )}
        style={{ transformOrigin: 'bottom right' }}
      >
        <div className="size-full rotate-180 -scale-y-100">
          <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 100.757 100.566">
            <defs>
              <filter
                colorInterpolationFilters="sRGB"
                filterUnits="userSpaceOnUse"
                height="108.233"
                id={id('leftEarFilter')}
                width="123.756"
                x="-22.9992"
                y="-7.6664"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
                <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
                <feOffset dx="-29.899" dy="-7.6664" />
                <feGaussianBlur stdDeviation="11.5" />
                <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.462745 0 0 0 0 0.168627 0 0 0 0 0.980392 0 0 0 1 0" />
                <feBlend in2="shape" mode="normal" result="effect1_innerShadow" />
              </filter>
            </defs>
            <g filter={`url(#${id('leftEarFilter')})`}>
              <path d={hoodiePaths.ear} fill="#BD98FF" data-region="ear" style={PE_AUTO} />
            </g>
          </svg>
        </div>
      </div>
    </>
  );
}

interface LegacyRainaEarsProps {
  svgId: string;
  isEarTwitching?: boolean;
  isNodding?: boolean;
  isHeadTilting?: boolean;
  headTiltDirection?: 'left' | 'right';
  onEarTwitchEnd?: () => void;
}

/**
 * Standalone ear rendering component for use in badge variant.
 * Renders ears separately so they can be layered outside the hexagon clip path.
 * Includes head animation wrapper for proper synchronization.
 */
export function LegacyRainaEars({
  svgId,
  isEarTwitching = false,
  isNodding = false,
  isHeadTilting = false,
  headTiltDirection = 'right',
  onEarTwitchEnd,
}: LegacyRainaEarsProps) {
  // Match head animation class for ear movement sync
  const headAnimationClass = isNodding
    ? 'motion-safe:animate-[raina-nod_0.7s_ease-in-out]'
    : isHeadTilting
      ? headTiltDirection === 'right'
        ? 'motion-safe:animate-[rainaHeadTiltRight_1s_ease-in-out]'
        : 'motion-safe:animate-[rainaHeadTiltLeft_1s_ease-in-out]'
      : '';

  return (
    <div className="absolute inset-0 pointer-events-none">
      <div
        className={cn('absolute inset-0 pointer-events-none', headAnimationClass)}
        style={{ transformOrigin: '50% 70%', willChange: 'transform' }}
      >
        <LegacyRainaEarsInner svgId={svgId} isEarTwitching={isEarTwitching} onEarTwitchEnd={onEarTwitchEnd} idSuffix="Badge" />
      </div>
    </div>
  );
}
